/* eslint-disable react/require-default-props */
import React, { useMemo } from 'react';
import {
  Radio,
  Row,
  Select,
} from 'antd';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Colors from '../../../constants/Colors';
import { filterSelectDropdown, getIdMap, isNullOrUndefined } from '../../../helpers/helpers';
import OnTraccrCheckbox from '../../../common/inputs/OnTraccrCheckbox';
import OnTraccrRadioGroup from '../../../common/inputs/OnTraccrRadioGroup';
import { formatEquipmentLabelFromCompanySettings } from '../../../equipment/equipment.helpers';

function additionalLabels(labels) {
  if (labels?.length) {
    return labels.map((label) => (
      <div
        key={label}
        className="dropdown-option-sub-label"
        style={{ color: Colors.ONTRACCR_OPACITY_GRAY, fontSize: 12 }}
      >
        {label}
      </div>
    ));
  }
  return null;
}

export default function DropdownFieldSelect({
  style,
  dropdownStyle,
  placeholder,
  numAnswers,
  openLimit,
  onChange,
  value,
  disabled,
  options,
  actualSelected,
  actualSelectedSet,
  isInline = false,
  projectIdMap,
  isExternalForm,
  dataType,
  layout = 'vertical',
  isFieldDisplay = false,
  searchByProjectAddress = false,
}) {
  const { settings = {} } = useSelector((state) => state.settings.company);

  const isEquipmentTable = dataType === 'Equipment';
  const isProjectTable = dataType === 'Projects';

  const dropdownOptions = useMemo(() => (
    options.map(({
      id: optionId,
      name: optionName,
      number: optionNumber,
      label: optionLabel,
      availability: optionAvailability,
      status: optionStatus,
      currentProjectId: optionProjectId,
      currentLocationText: optionLocationText,
      subNames,
      code: optionCode,
      address: optionAddress,
    }) => {
      const label = optionLabel ?? optionName;

      const equipmentLabels = [];
      if (isEquipmentTable && !isExternalForm) {
        equipmentLabels.push(`Status: ${optionStatus}`);
        equipmentLabels.push(`Available?: ${optionAvailability ? 'Yes' : 'No'}`);
        if (!optionAvailability) {
          const currentAssignment = optionProjectId
            ? `Project: ${projectIdMap[optionProjectId]?.name}`
            : `Location: ${optionLocationText}`;
          equipmentLabels.push(currentAssignment);
        }
      }

      const formattedLabel = isEquipmentTable
        ? formatEquipmentLabelFromCompanySettings({
          name: optionName,
          code: optionCode,
          settings,
        })
        : label;

      if (isFieldDisplay) return { label: formattedLabel, id: optionId };

      const projectAddressLabels = (isProjectTable && searchByProjectAddress && optionAddress)
        ? [optionAddress]
        : null;

      return (
        <Select.Option
          disabled={
            !openLimit
            && numAnswers > 1
            && actualSelected.length >= numAnswers
            && !actualSelectedSet.has(optionId)
          }
          value={optionId}
          key={optionId}
          label={formattedLabel}
          name={optionName}
          number={optionNumber}
          address={isProjectTable && searchByProjectAddress ? (optionAddress ?? '') : undefined}
        >
          {formattedLabel}
          {additionalLabels(subNames)}
          {additionalLabels(equipmentLabels)}
          {additionalLabels(projectAddressLabels)}
        </Select.Option>
      );
    })
  ), [
    options,
    isEquipmentTable,
    isProjectTable,
    isExternalForm,
    projectIdMap,
    settings,
    openLimit,
    numAnswers,
    actualSelected,
    actualSelectedSet,
    isFieldDisplay,
    searchByProjectAddress,
  ]);

  const optionsMap = useMemo(() => getIdMap(dropdownOptions), [dropdownOptions]);

  const selectedNameMap = useMemo(() => {
    const map = {};
    actualSelected?.forEach((selected) => {
      if (isNullOrUndefined(selected?.id) || isNullOrUndefined(selected?.name)) {
        return;
      }
      map[selected.id] = selected.name;
    });
    return map;
  }, [actualSelected]);

  if (isFieldDisplay && dropdownStyle === 'dropdown') {
    return (
      <div>
        {value?.map((option, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className="form-field-display-value-text" key={index}>
            {value.length > 1 ? '- ' : ''}{optionsMap[option]?.label ?? selectedNameMap[option] ?? option}
          </div>
        )) ?? []}
      </div>
    );
  }

  switch (dropdownStyle) {
    case 'dropdown': {
      const filterOption = (isProjectTable && searchByProjectAddress)
        ? (input, option) => {
          const lowercaseInput = input?.toLowerCase() ?? '';
          const lowercaseAddress = String(option?.address ?? '').toLowerCase();
          return filterSelectDropdown(input, option) || lowercaseAddress.includes(lowercaseInput);
        }
        : filterSelectDropdown;

      return (
        <Select
          style={style}
          placeholder={placeholder}
          mode={numAnswers > 1 || openLimit ? 'multiple' : null}
          onChange={onChange}
          value={value}
          optionFilterProp="label"
          showSearch
          allowClear
          disabled={disabled}
          filterOption={filterOption}
        >
          {dropdownOptions}
        </Select>
      );
    }
    case 'radio': {
      const formattedValue = value?.length ? value[0] : value;
      const isVertical = layout === 'vertical';
      const shouldBeOneRow = !isVertical && dropdownStyle === 'radio' && options.length <= 4;
      const DivComponent = shouldBeOneRow ? React.Fragment : Row;

      return (
        <OnTraccrRadioGroup
          disabled={disabled}
          value={formattedValue}
          onChange={onChange}
          style={isInline ? { display: 'flex' } : {}}
        >
          {options.map(({ id, name, label }) => (
            <DivComponent key={id} style={isVertical ? { display: 'block' } : {}}>
              <Radio
                value={id}
              >
                {label ?? name}
              </Radio>
            </DivComponent>
          ))}
        </OnTraccrRadioGroup>
      );
    }
    case 'checkbox': {
      const selectedSet = new Set(value);
      const isVertical = layout === 'vertical';
      const onChangeCheckbox = (id) => (checked) => {
        const newValue = [...(value || [])];
        if (checked) {
          newValue.push(id);
        } else {
          const index = newValue.indexOf(id);
          if (index > -1) {
            newValue.splice(index, 1);
          }
        }
        onChange(newValue);
      };

      return (
        <div style={{ display: isVertical ? 'block' : 'flex', flexDirection: isVertical ? 'column' : 'row' }}>
          {options.map(({ id, name, label }) => (
            <Row key={id} style={isVertical ? { display: 'block', marginBottom: 8 } : {}}>
              <OnTraccrCheckbox
                onChange={onChangeCheckbox(id)}
                value={selectedSet.has(id)}
                disabled={
                  disabled || (
                    !openLimit
                    && actualSelected.length >= numAnswers
                    && !actualSelectedSet.has(id)
                  )
                }
                label={label ?? name}
                checkboxStyle={isInline ? { display: 'flex' } : {}}
              />
            </Row>
          ))}
        </div>
      );
    }
    default:
      return null;
  }
}

DropdownFieldSelect.propTypes = {
  style: PropTypes.shape({}),
  dropdownStyle: PropTypes.string,
  placeholder: PropTypes.string,
  numAnswers: PropTypes.number,
  openLimit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  actualSelected: PropTypes.arrayOf(PropTypes.string),
  actualSelectedSet: PropTypes.instanceOf(Set),
  settings: PropTypes.shape({}),
  isInline: PropTypes.bool,
  projectIdMap: PropTypes.shape({}),
  isExternalForm: PropTypes.bool,
  dataType: PropTypes.string,
  layout: PropTypes.string,
  isFieldDisplay: PropTypes.bool,
  searchByProjectAddress: PropTypes.bool,
};

DropdownFieldSelect.defaultProps = {
  style: {},
  dropdownStyle: 'dropdown',
  placeholder: '',
  numAnswers: 1,
  openLimit: false,
  value: [],
  disabled: false,
  options: [],
  actualSelected: [],
  actualSelectedSet: new Set(),
  settings: {},
  isInline: false,
  projectIdMap: {},
  isExternalForm: false,
  dataType: '',
  layout: 'vertical',
  searchByProjectAddress: false,
};
